import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getPublicServices,
  searchServicesMunicipaux,
} from "../../../services/servicesMunicipauxServices";

import {
  ensureArray,
  normalizeSearchText,
  normalizeServiceMunicipal,
  sortServices,
} from "../utils/servicesMunicipauxUtils";

const INITIAL_FILTERS = {
  q: "",
  categorie: "",
  sort: "nom",
};

export function useServicesMunicipauxPage() {
  const bootedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allItems, setAllItems] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [expandedMapId, setExpandedMapId] = useState(null);
  const [expandedDetailsId, setExpandedDetailsId] = useState(null);

  const deferredSearch = useDeferredValue(filters.q);

  const loadServices = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      let response = await getPublicServices();

      if (!Array.isArray(response)) {
        const searchResponse = await searchServicesMunicipaux({
          is_active: true,
          is_public: true,
          limit: 300,
          page: 1,
          sort: "ordre_affichage",
          dir: "ASC",
        });

        response = searchResponse?.items || [];
      }

      const rows = ensureArray(response).map(normalizeServiceMunicipal);

      setAllItems(sortServices(rows, "nom"));
    } catch (err) {
      console.error("Erreur chargement services municipaux:", err);
      setAllItems([]);
      setError("LOAD_ERROR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    loadServices();
  }, [loadServices]);

  const categories = useMemo(() => {
    return Array.from(
      new Set(
        allItems
          .map((service) => service.category)
          .filter(Boolean)
          .map((category) => String(category).trim())
      )
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [allItems]);

  const filteredItems = useMemo(() => {
    const q = normalizeSearchText(deferredSearch);

    const filtered = allItems.filter((service) => {
      const matchSearch = q ? service.searchIndex.includes(q) : true;

      const matchCategory = filters.categorie
        ? service.category === filters.categorie
        : true;

      return matchSearch && matchCategory;
    });

    return sortServices(filtered, filters.sort);
  }, [allItems, deferredSearch, filters.categorie, filters.sort]);

  const updateFilter = useCallback((name, value) => {
    setExpandedMapId(null);
    setExpandedDetailsId(null);

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  }, []);

  const toggleCategory = useCallback((category) => {
    setExpandedMapId(null);
    setExpandedDetailsId(null);

    setFilters((previous) => ({
      ...previous,
      categorie: previous.categorie === category ? "" : category,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setExpandedMapId(null);
    setExpandedDetailsId(null);
  }, []);

  const toggleMap = useCallback((serviceId) => {
    setExpandedMapId((current) => (current === serviceId ? null : serviceId));
  }, []);

  const toggleDetails = useCallback((serviceId) => {
    setExpandedDetailsId((current) =>
      current === serviceId ? null : serviceId
    );
  }, []);

  const refresh = useCallback(() => {
    setExpandedMapId(null);
    setExpandedDetailsId(null);
    loadServices();
  }, [loadServices]);

  return {
    loading,
    error,

    items: filteredItems,
    total: filteredItems.length,
    allTotal: allItems.length,

    categories,
    filters,

    expandedMapId,
    expandedDetailsId,

    updateFilter,
    toggleCategory,
    resetFilters,
    toggleMap,
    toggleDetails,
    refresh,
  };
}